import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LineData } from './LineData'
import { RoomType } from '../availability-tab/type'

interface Props {
  room: RoomType
  close: () => void
}

export const RoomInfo = ({ room, close }: Props): React.JSX.Element => {
  const [isOpenFloorImage, setIsOpenFloorImage] = useState(false)
  const { t } = useTranslation()
  const fsLightboxWindow = window as Window & {
    refreshFsLightbox?: () => void
    fsLightboxInstances?: Record<
      string,
      { props: { onSourceLoad?: (instance: unknown, source: HTMLElement, index: number) => void } }
    >
  }

  // キャプションの付け方は client/lp_photos.ts の applyLightbox と同じ（fslightbox 3.8.0 対応の理由もそちら）
  useEffect(() => {
    fsLightboxWindow.refreshFsLightbox?.()

    const galleryName = `room-gallery-${room.id}`
    const lightbox = fsLightboxWindow.fsLightboxInstances?.[galleryName]
    if (!lightbox) return

    const allImages = document.querySelectorAll<HTMLAnchorElement>(`[data-fslightbox="${galleryName}"]`)
    lightbox.props.onSourceLoad = (_instance, source, index) => {
      const sourceWrapper = source.parentElement
      if (!sourceWrapper) return

      sourceWrapper.classList.add('lp-lightbox-source')
      const caption = allImages[index]?.dataset.caption
      if (!caption || sourceWrapper.querySelector('.fslightbox-caption')) return

      const captionDiv = document.createElement('div')
      captionDiv.className = 'fslightbox-caption'
      captionDiv.textContent = caption
      sourceWrapper.appendChild(captionDiv)
    }
  }, [room.photos])

  return (
    <>
      <div className="modal-backdrop fixed z-50 top-0 left-0 right-0 w-full overflow-x-hidden overflow-y-auto max-h-full h-full flex md:inset-0">
        <div className="flex flex-col items-start gap-4 p-3 bg-white overflow-y-auto fixed top-0 right-0 bottom-0 right-drawer">
          <div className="overflow-y-auto w-full">
            <div className="flex mb-3">
              <div className="font-bold font-size-16 text-black-900 mt-2">{t('landing_page.avaiability.room.drawer_title')}</div>
              <button
                className="text-gray-900 bg-transparent hover:bg-gray-200 rounded-lg text-sm ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white p-1.5"
                type="button"
                onClick={close}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path
                    clipRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    fillRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            <div className="flex gap-2 overflow-auto w-full mb-3">
              {room?.photos?.map((photo, index) => (
                <a
                  key={`room_image_${index}`}
                  href={photo.url}
                  data-fslightbox={`room-gallery-${room.id}`}
                  data-caption={photo.caption || ''}
                  className="block flex-none"
                >
                  <img src={photo.url} alt={`Room Image ${index + 1}`} className="rounded-lg object-cover h-50 cursor-pointer" />
                </a>
              ))}
            </div>

            <div className="flex flex-col items-start gap-4 w-full">
              <div className="flex flex-col items-start gap-2 w-full">
                <div className="font-bold font-size-16 text-black-900">{room.name}</div>
                {room.description && (
                  <p
                    className="text-gray-500 mb-2"
                    dangerouslySetInnerHTML={{ __html: room.description.replaceAll(/\n/g, '<br />') }}
                  />
                )}
                <table className="w-full">
                  <tbody>
                    <LineData label={t('plan_detail.room_info.accommodation_type')} value={room.roomType} />
                    <LineData
                      label={t('plan_detail.room_info.capacity.title')}
                      value={`${t('landing_page.avaiability.room.max_capacity_text', { count: room.roomMaxCount })}`}
                    />
                    <LineData label={t('plan_detail.room_info.kitchen')} value={room.kitchen} />
                    <LineData label={t('plan_detail.room_info.bedroom')} value={room.bedroom} />
                    <LineData label={t('landing_page.avaiability.room.bathroom')} value={room.bathroom} />
                    <LineData label={t('plan_detail.room_info.laundry')} value={room.laundry} />
                    <LineData label={t('landing_page.avaiability.room.food')} value={room.food} />
                    <LineData label={t('landing_page.avaiability.room.environment')} value={room.roomEnvironment} />
                    <LineData label={t('landing_page.avaiability.room.work_environment')} value={room.conditionsForRemoteWork} />
                    <LineData label={t('landing_page.avaiability.room.access_way')} value={room.accessToNursery} />
                  </tbody>
                </table>
              </div>

              {room?.floorImages?.length > 0 && (
                <div className="w-full mb-6">
                  <button
                    className="flex items-center justify-center py-3 rounded-sm border border-solid border-green w-full"
                    onClick={() => setIsOpenFloorImage(true)}
                  >
                    <div className="flex items-center justify-center w-full gap-2">
                      <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_15_113)">
                          <path
                            d="M9.5 11H7.5V13H9.5V11ZM13.5 11H11.5V13H13.5V11ZM17.5 11H15.5V13H17.5V11ZM19.5 4H18.5V2H16.5V4H8.5V2H6.5V4H5.5C4.39 4 3.51 4.9 3.51 6L3.5 20C3.5 21.1 4.39 22 5.5 22H19.5C20.6 22 21.5 21.1 21.5 20V6C21.5 4.9 20.6 4 19.5 4ZM19.5 20H5.5V9H19.5V20Z"
                            fill="#189855"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_15_113">
                            <rect width="24" height="24" fill="white" transform="translate(0.5)" />
                          </clipPath>
                        </defs>
                      </svg>
                      <span className="w-fit font-regular-regular14 whitespace-nowrap text-green">
                        {t('landing_page.avaiability.room.check_floor_plan')}
                      </span>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {isOpenFloorImage && (
        <div className="modal-backdrop fixed z-50 top-0 left-0 right-0 w-full h-full max-h-full overflow-hidden">
          <div className="flex flex-col items-center h-full">
            <div className="relative m-auto text-center">
              <button
                className="absolute right-2"
                type="button"
                onClick={() => setIsOpenFloorImage(false)}
                style={{ top: '10px' }}
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path
                    clipRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    fillRule="evenodd"
                  />
                </svg>
              </button>
              <img src={room.floorImages[0]} alt="Floor Plan" style={{ maxHeight: '400px' }} />
            </div>
          </div>
        </div>
      )}
    </>
  )
}
